import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { PHRASE_STORAGE_KEY, readPhrases, removePhrase, writePhrases } from '@chat-rea/console-ui';
import {
  listFleetMessages,
  listFleetSessions,
  openFleetWs,
  FleetHttpError,
  sayAsHuman,
  setSessionMode,
  type FleetMessage,
  type FleetSession,
} from '../../lib/fleetApi';
import { FUNNEL_PLATFORMS } from '../../lib/funnel';
import { outcomeBadges } from '../../lib/outcomeEvents';
import { readRoomMeta } from '../../lib/roomMeta';
import {
  assigneeText,
  clearDraft,
  composerKeyAction,
  confirmReturnAuto,
  defaultSessionId,
  dismissTakeoverArm,
  draftText,
  filterSessionsByLife,
  formatRelativeZhTw,
  headerModeClick,
  listKeyMove,
  markSessionMode,
  mergeDirected,
  mergeLastBodies,
  mergeSessionsById,
  nextNeedsReplyId,
  PENDING_REPLY_CHIP,
  pendingReplyBanner,
  pendingReplyCount,
  planHumanSend,
  rememberDraft,
  repliesBySession,
  replyDotVisible,
  sayErrorBesideComposer,
  sessionEnded,
  sessionHeading,
  sortSessionsByNeedsReply,
  type DraftMap,
  type LifeFilter,
  type TakeoverArm,
} from './waterfall-list';
import { platformChip, sessionDetail } from './waterfall-detail';
import { localSeenStore, readLastSeen, writeLastSeen } from './waterfall-seen';
import {
  waterfallPhraseView,
  type PhraseEnter,
  type PhraseMenuItem,
  type PhraseView,
} from './waterfall-phrases';

const NEAR_BOTTOM_PX = 48;

type WaterfallPageProps = {
  linkedSessionId: string | null;
  linkedPlatform: string | null;
  onSelectSession: (sessionId: string | null) => void;
  onPlatformChange: (platform: string | null) => void;
};

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'TEXTAREA' || tag === 'INPUT' || tag === 'SELECT';
}

function upsertMessage(prev: FleetMessage[], next: FleetMessage): FleetMessage[] {
  const index = prev.findIndex((message) => message.id === next.id);
  if (index < 0) return [...prev, next].slice(-200);
  const prior = prev[index];
  if (!prior) return prev;
  const copy = prev.slice();
  copy[index] = { ...prior, ...next, meta: next.meta ?? prior.meta };
  return copy;
}

function readMode(value: unknown): 'auto' | 'human' | null {
  if (value === 'auto' || value === 'human') return value;
  return null;
}

function browserPhraseStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function phraseSubmitLabel(enter: PhraseEnter, takeover: boolean): string {
  switch (enter.kind) {
    case 'insert':
      return '帶入';
    case 'save':
      return '存片語';
    case 'unsave':
      return '刪片語';
    case 'hold':
    case 'send':
      return takeover ? '仍要送出' : '送出';
    default: {
      const unreachable: never = enter;
      return unreachable;
    }
  }
}

function phraseSubmitDisabled(view: PhraseView, draft: string, busy: boolean): boolean {
  switch (view.enter.kind) {
    case 'hold':
      return true;
    case 'send':
      return busy || draft.trim() === '';
    case 'insert':
    case 'save':
    case 'unsave':
      return false;
    default: {
      const unreachable: never = view.enter;
      return unreachable;
    }
  }
}

export function WaterfallPage({
  linkedSessionId,
  linkedPlatform,
  onSelectSession,
  onPlatformChange,
}: WaterfallPageProps) {
  const [sessions, setSessions] = useState<FleetSession[]>([]);
  const [lifeFilter, setLifeFilter] = useState<LifeFilter>('all');
  const platformFilter = platformChip(linkedPlatform);
  const platformChips = ['all', ...FUNNEL_PLATFORMS] as const;
  const [messages, setMessages] = useState<FleetMessage[]>([]);
  const [sessionOutcomes, setSessionOutcomes] = useState<string[]>([]);
  const [outcomeSessionId, setOutcomeSessionId] = useState<string | null>(null);
  const [directed, setDirected] = useState<FleetMessage[]>([]);
  const [seen, setSeen] = useState<Record<string, string>>(() => readLastSeen(localSeenStore()));
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [drafts, setDrafts] = useState<DraftMap>({});
  const [activeId, setActiveId] = useState<string | null>(linkedSessionId);
  const [listReady, setListReady] = useState(false);
  const [armed, setArmed] = useState<TakeoverArm>('none');
  const [composerError, setComposerError] = useState<string | null>(null);
  const [phraseTick, setPhraseTick] = useState(0);
  const [phraseNote, setPhraseNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const sendLock = useRef(false);
  const armedRef = useRef(armed);
  const appliedDefault = useRef(linkedSessionId !== null);
  const onSelectRef = useRef(onSelectSession);
  const activeRef = useRef(activeId);
  const streamRef = useRef<HTMLDivElement>(null);
  const stickRef = useRef(true);
  onSelectRef.current = onSelectSession;
  activeRef.current = activeId;
  armedRef.current = armed;

  const active = useMemo(
    () => sessions.find((session) => session.sessionId === activeId) ?? null,
    [sessions, activeId],
  );
  const detail = useMemo(() => {
    if (!active) return null;
    const thread = messages.filter((message) => message.sessionId === active.sessionId);
    const events = outcomeSessionId === active.sessionId ? sessionOutcomes : [];
    return sessionDetail(active, thread, events);
  }, [active, messages, outcomeSessionId, sessionOutcomes]);
  const replyBySession = useMemo(() => repliesBySession(directed), [directed]);
  const replyRef = useRef(replyBySession);
  replyRef.current = replyBySession;
  const needyIds = useMemo(() => {
    const ids = new Set<string>();
    for (const [sessionId, reply] of replyBySession) {
      if (reply.needsReply) ids.add(sessionId);
    }
    return ids;
  }, [replyBySession]);
  const ordered = useMemo(
    () => sortSessionsByNeedsReply(filterSessionsByLife(sessions, lifeFilter), needyIds),
    [sessions, lifeFilter, needyIds],
  );
  const pendingLabel = useMemo(
    () => pendingReplyBanner(pendingReplyCount(ordered.map((session) => session.sessionId), needyIds)),
    [ordered, needyIds],
  );
  const showTakeoverWarn = armed === 'leave-auto' && active?.mode === 'auto';
  const showReturnConfirm = active !== null && confirmReturnAuto(active.mode, armed);
  const activeAssignee = assigneeText(active?.assignee);
  const draft = draftText(drafts, activeId);
  const phrases = useMemo(() => readPhrases(browserPhraseStorage()), [phraseTick, draft]);
  const phraseView = useMemo(() => waterfallPhraseView(draft, phrases), [draft, phrases]);

  const sessionReq = useRef(0);
  const modeHold = useRef<Record<string, number>>({});
  const refreshSessions = useCallback(async () => {
    const ticket = ++sessionReq.current;
    try {
      const { sessions: rows } = await listFleetSessions(
        platformFilter === 'all' ? {} : { platform: platformFilter },
      );
      if (ticket !== sessionReq.current) return;
      setSessions((prev) => mergeSessionsById(prev, rows, ticket, modeHold.current));
      // 成功不在這裡清 error：訊息輪詢失敗的紅字不該被 session 成功蓋掉。
    } catch (err) {
      if (ticket !== sessionReq.current) return;
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [platformFilter]);

  useEffect(() => {
    void refreshSessions();
  }, [platformFilter, refreshSessions]);

  const messageReq = useRef(0);
  const refreshMessages = useCallback(async (sessionId: string | null) => {
    const ticket = ++messageReq.current;
    try {
      const focused = await listFleetMessages(sessionId ? { sessionId, limit: 120 } : { limit: 80 });
      const broad = sessionId ? (await listFleetMessages({ limit: 80 })).messages : [];
      if (ticket !== messageReq.current) return;
      const rows = focused.messages;
      const incoming = [...rows, ...broad];
      setMessages(rows);
      setOutcomeSessionId(sessionId);
      setSessionOutcomes(sessionId ? outcomeBadges((focused.outcomes ?? []).map((row) => row.event)) : []);
      setDirected((prev) => mergeDirected(prev, incoming));
      setPreviews((prev) => mergeLastBodies(prev, incoming));
      setListReady(true);
      setError(null);
    } catch (err) {
      if (ticket !== messageReq.current) return;
      setListReady(true);
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const markSeen = useCallback((sessionId: string, ts: string) => {
    setSeen(writeLastSeen(localSeenStore(), sessionId, ts));
  }, []);

  const selectSession = useCallback(
    (sessionId: string | null) => {
      appliedDefault.current = true;
      setActiveId(sessionId);
      setArmed('none');
      setComposerError(null);
      setPhraseNote(null);
      onSelectRef.current(sessionId);
      if (!sessionId) return;
      const ts = replyRef.current.get(sessionId)?.latestInboundTs;
      if (ts) markSeen(sessionId, ts);
    },
    [markSeen],
  );

  useEffect(() => {
    if (linkedSessionId === activeRef.current) return;
    appliedDefault.current = true;
    setActiveId(linkedSessionId);
    setArmed('none');
    setComposerError(null);
    setPhraseNote(null);
  }, [linkedSessionId]);

  useEffect(() => {
    if (appliedDefault.current || !listReady || sessions.length === 0) return;
    const next = defaultSessionId(sessions, messages);
    appliedDefault.current = true;
    if (!next) return;
    setActiveId(next);
    setArmed('none');
    setComposerError(null);
    onSelectRef.current(next);
  }, [listReady, sessions, messages]);

  useEffect(() => {
    const bump = () => setPhraseTick((n) => n + 1);
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === PHRASE_STORAGE_KEY) bump();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', bump);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', bump);
    };
  }, []);

  useEffect(() => {
    void refreshSessions();
    void refreshMessages(activeId);
    const poll = window.setInterval(() => {
      setNowMs(Date.now());
      void refreshSessions();
      void refreshMessages(activeId);
    }, 2500);
    const ws = openFleetWs((raw) => {
      if (typeof raw !== 'object' || raw === null) return;
      const ev = raw as Record<string, unknown>;
      const type = typeof ev.type === 'string' ? ev.type : '';
      const sessionId = typeof ev.sessionId === 'string' ? ev.sessionId : '';
      if (type === 'message' && sessionId && typeof ev.body === 'string' && typeof ev.id === 'string' && typeof ev.ts === 'string') {
        const next: FleetMessage = {
          id: ev.id,
          sessionId,
          platform: typeof ev.platform === 'string' ? ev.platform : 'wootalk',
          direction: typeof ev.direction === 'string' ? ev.direction : 'in',
          actor: typeof ev.actor === 'string' ? ev.actor : 'peer',
          body: ev.body,
          ts: ev.ts,
          pendingSend: ev.pendingSend === true,
          meta: readRoomMeta(ev.meta),
        };
        setMessages((prev) => upsertMessage(prev, next));
        setDirected((prev) => mergeDirected(prev, [next]));
        setPreviews((prev) => mergeLastBodies(prev, [next]));
        void refreshSessions();
      }
      if (type === 'mode' && sessionId) {
        const mode = readMode(ev.mode);
        if (!mode) return;
        const assignee = mode === 'human' ? assigneeText(typeof ev.assignee === 'string' ? ev.assignee : null) : null;
        noteSessionMode(sessionId, mode, assignee);
        if (sessionId === activeRef.current && mode === 'auto') setArmed('none');
      }
      if (type === 'session_end' && sessionId) {
        const endedAt = typeof ev.endedAt === 'string' ? ev.endedAt : new Date().toISOString();
        setSessions((prev) =>
          prev.map((row) => (row.sessionId === sessionId ? { ...row, endedAt } : row)),
        );
      }
    });
    return () => {
      window.clearInterval(poll);
      ws?.close();
    };
  }, [activeId, refreshMessages, refreshSessions]);

  useEffect(() => {
    const ids = ordered.map((session) => session.sessionId);
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape' && !event.altKey && !event.ctrlKey && !event.metaKey && !event.isComposing) {
        const next = dismissTakeoverArm(armedRef.current, event.key);
        if (next !== armedRef.current) {
          event.preventDefault();
          setArmed(next);
        }
        return;
      }
      if (event.altKey || event.ctrlKey || event.metaKey || event.isComposing) return;
      if (isTypingTarget(event.target)) return;
      if (event.key === 'Tab' && !event.shiftKey) {
        const next = nextNeedsReplyId(ids, needyIds, activeId);
        if (!next) return;
        event.preventDefault();
        selectSession(next);
        return;
      }
      const move = listKeyMove(ids, activeId, event.key);
      switch (move.kind) {
        case 'pass':
          return;
        case 'select':
          event.preventDefault();
          if (move.sessionId !== activeId) selectSession(move.sessionId);
          return;
        default: {
          const unreachable: never = move;
          return unreachable;
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeId, needyIds, ordered, selectSession]);

  function noteSessionMode(sessionId: string, mode: 'auto' | 'human', assignee?: string | null) {
    const ticket = ++sessionReq.current;
    if (mode === 'human') modeHold.current = { ...modeHold.current, [sessionId]: ticket };
    else if (Object.prototype.hasOwnProperty.call(modeHold.current, sessionId)) {
      const next = { ...modeHold.current };
      delete next[sessionId];
      modeHold.current = next;
    }
    setSessions((prev) => {
      const marked = markSessionMode(prev, sessionId, mode);
      if (assignee === undefined) return marked;
      return marked.map((session) => (session.sessionId === sessionId ? { ...session, assignee } : session));
    });
  }

  function noteFault(err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    const status = err instanceof FleetHttpError ? err.status : 0;
    if (sayErrorBesideComposer(status)) {
      setComposerError(message);
      return;
    }
    setComposerError(null);
    setError(message);
  }

  async function commitMode(next: 'auto' | 'human') {
    if (!active) return;
    setBusy(true);
    try {
      await setSessionMode(active.sessionId, next, active.platform);
      noteSessionMode(active.sessionId, next);
      setArmed('none');
      setComposerError(null);
      await refreshSessions();
    } catch (err) {
      noteFault(err);
    } finally {
      setBusy(false);
    }
  }

  function requestMode() {
    if (!active || busy) return;
    const click = headerModeClick(active.mode);
    switch (click) {
      case 'to-human':
        void commitMode('human');
        return;
      case 'ask-auto':
        setArmed('return-auto');
        setComposerError(null);
        return;
      default: {
        const unreachable: never = click;
        return unreachable;
      }
    }
  }

  function confirmReturn() {
    if (!active || busy || !confirmReturnAuto(active.mode, armed)) return;
    void commitMode('auto');
  }

  async function commitSend() {
    if (sendLock.current || !active || !draft.trim()) return;
    sendLock.current = true;
    setBusy(true);
    stickRef.current = true;
    const sessionId = active.sessionId;
    const text = draft.trim();
    try {
      if (active.mode !== 'human') {
        await setSessionMode(sessionId, 'human', active.platform);
        noteSessionMode(sessionId, 'human');
      }
      await sayAsHuman(sessionId, text, active.platform);
      const seenTs = replyRef.current.get(sessionId)?.latestInboundTs ?? new Date().toISOString();
      markSeen(sessionId, seenTs);
      setDrafts((prev) => clearDraft(prev, sessionId));
      setArmed('none');
      setComposerError(null);
      await refreshMessages(sessionId);
      await refreshSessions();
    } catch (err) {
      noteFault(err);
    } finally {
      sendLock.current = false;
      setBusy(false);
    }
  }

  function requestSend() {
    if (sendLock.current || !active || !draft.trim() || busy) return;
    const plan = planHumanSend(active.mode, armed === 'leave-auto');
    switch (plan) {
      case 'confirm':
        setArmed('leave-auto');
        setComposerError(null);
        return;
      case 'send':
        void commitSend();
        return;
      default: {
        const unreachable: never = plan;
        return unreachable;
      }
    }
  }

  function freshPhraseView(): PhraseView {
    return waterfallPhraseView(draft, readPhrases(browserPhraseStorage()));
  }

  function applyPhrase(view: PhraseView) {
    const sessionId = active?.sessionId;
    if (!sessionId) return;
    const enter = view.enter;
    switch (enter.kind) {
      case 'send':
        requestSend();
        return;
      case 'insert':
        setDrafts((prev) => rememberDraft(prev, sessionId, enter.text));
        setPhraseNote(null);
        return;
      case 'save':
      case 'unsave':
        writePhrases(browserPhraseStorage(), enter.phrases);
        setPhraseTick((n) => n + 1);
        setDrafts((prev) => clearDraft(prev, sessionId));
        setPhraseNote(enter.notice);
        return;
      case 'hold':
        setPhraseNote(enter.notice);
        return;
      default: {
        const unreachable: never = enter;
        return unreachable;
      }
    }
  }

  function pickMenuItem(item: PhraseMenuItem) {
    const sessionId = active?.sessionId;
    if (!sessionId) return;
    if (item.pick === 'insert') {
      setDrafts((prev) => rememberDraft(prev, sessionId, item.text));
      setPhraseNote(null);
      return;
    }
    writePhrases(browserPhraseStorage(), removePhrase(phrases, item.text));
    setPhraseTick((n) => n + 1);
    setDrafts((prev) => clearDraft(prev, sessionId));
    setPhraseNote(`已刪掉片語「${item.text}」`);
  }

  function onComposerKeyDown(event: ReactKeyboardEvent<HTMLTextAreaElement>) {
    if (
      event.key === 'Tab' &&
      !event.shiftKey &&
      !event.altKey &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      const el = event.currentTarget;
      const start = el.selectionStart ?? draft.length;
      const end = el.selectionEnd ?? start;
      const sessionId = active?.sessionId;
      if (!sessionId) return;
      const next = `${draft.slice(0, start)}\t${draft.slice(end)}`;
      setDrafts((prev) => rememberDraft(prev, sessionId, next));
      const caret = start + 1;
      window.requestAnimationFrame(() => {
        el.selectionStart = caret;
        el.selectionEnd = caret;
      });
      return;
    }
    const action = composerKeyAction({
      key: event.key,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
      isComposing: event.nativeEvent.isComposing,
      repeat: event.repeat,
    });
    switch (action) {
      case 'hold':
        event.preventDefault();
        event.stopPropagation();
        return;
      case 'newline':
        return;
      case 'send':
        event.preventDefault();
        applyPhrase(freshPhraseView());
        return;
      case 'pass':
        return;
      default: {
        const unreachable: never = action;
        return unreachable;
      }
    }
  }

  const shown = useMemo(
    () => (activeId ? messages.filter((message) => message.sessionId === activeId) : messages),
    [activeId, messages],
  );
  const tailId = shown.at(-1)?.id ?? '';

  function onStreamScroll() {
    const el = streamRef.current;
    if (!el) return;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
  }

  useLayoutEffect(() => {
    stickRef.current = true;
    const el = streamRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [activeId]);

  useLayoutEffect(() => {
    if (!stickRef.current) return;
    const el = streamRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [activeId, tailId, shown.length]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-hidden p-3" data-testid="waterfall-page">
      <header className="flex shrink-0 flex-wrap items-end justify-between gap-2">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-text-tertiary">導流工作台</p>
          <h1 className="font-serif text-xl text-text">訊息瀑布</h1>
          <p className="mt-1 text-xs text-text-tertiary">
            每一條可切人工（暫停自動回覆）再切回自動。WS 推送＋輪詢雙保險。
          </p>
        </div>
        {active ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <span
              data-testid="waterfall-mode-badge"
              data-mode={active.mode}
              className={`rounded border border-border px-2 py-0.5 text-xs ${
                active.mode === 'human' ? 'text-warning' : 'text-text-tertiary'
              }`}
            >
              {active.mode === 'human' ? '人工' : '自動'}
            </span>
            {activeAssignee ? (
              <span className="text-xs text-text-tertiary" data-testid="waterfall-assignee">
                {activeAssignee}
              </span>
            ) : null}
            <button
              type="button"
              disabled={busy}
              onClick={requestMode}
              className="rounded border border-border px-3 py-1.5 text-sm"
              data-testid="mode-toggle"
            >
              {active.mode === 'human' ? '切回自動' : '改人工'}
            </button>
            {showReturnConfirm ? (
              <div className="flex flex-wrap items-center gap-2" role="alert" data-testid="return-auto-confirm">
                <span className="text-xs text-amber-400">切回自動後，自動回覆會恢復。</span>
                <button
                  type="button"
                  disabled={busy}
                  className="rounded border border-border px-2 py-1 text-xs"
                  data-testid="return-auto-commit"
                  onClick={confirmReturn}
                >
                  確定切回
                </button>
                <button
                  type="button"
                  className="rounded border border-border px-2 py-1 text-xs"
                  data-testid="return-auto-cancel"
                  onClick={() => setArmed('none')}
                >
                  留在人工
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </header>

      {error ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2 text-sm text-danger" role="alert">
          <span>{error}</span>
          <button
            type="button"
            className="rounded border border-border px-2 py-0.5 text-xs text-text"
            onClick={() => {
              void refreshSessions();
              void refreshMessages(activeId);
            }}
          >
            重試
          </button>
        </div>
      ) : null}

      {pendingLabel ? (
        <p
          className="shrink-0 rounded border border-warning px-3 py-2 text-sm text-warning"
          role="status"
          data-testid="waterfall-pending-banner"
        >
          {pendingLabel}
        </p>
      ) : null}

      <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[auto_minmax(0,1fr)] gap-3 md:grid-cols-[14rem_1fr] md:grid-rows-1">
        <aside className="max-h-36 overflow-auto rounded border border-border md:h-full md:max-h-none">
          <div className="flex flex-wrap gap-1 border-b border-border p-2" data-testid="waterfall-platform-filter">
            {platformChips.map((value) => (
              <button
                key={value}
                type="button"
                data-testid={`waterfall-platform-${value}`}
                data-selected={platformFilter === value ? 'true' : 'false'}
                className={`rounded border border-border px-2 py-0.5 text-[10px] ${
                  platformFilter === value ? 'bg-muted text-text' : 'text-text-tertiary'
                }`}
                onClick={() => onPlatformChange(value === 'all' ? null : value)}
              >
                {value === 'all' ? '全部平台' : value}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1 border-b border-border p-2" data-testid="waterfall-life-filter">
            {(
              [
                ['all', '全部'],
                ['alive', '進行中'],
                ['ended', '已離開'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                data-testid={`waterfall-life-${value}`}
                data-selected={lifeFilter === value ? 'true' : 'false'}
                className={`rounded border border-border px-2 py-0.5 text-[10px] ${
                  lifeFilter === value ? 'bg-muted text-text' : 'text-text-tertiary'
                }`}
                onClick={() => setLifeFilter(value)}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            data-testid="waterfall-all"
            data-selected={activeId === null ? 'true' : 'false'}
            className={`block w-full border-b border-border px-2 py-2 text-left text-xs ${
              activeId === null ? 'bg-muted' : ''
            }`}
            onClick={() => selectSession(null)}
          >
            全部動態
          </button>
          {ordered.map((session) => {
            const heading = sessionHeading(session);
            const preview = previews[session.sessionId] ?? '';
            const selected = activeId === session.sessionId;
            const rowAssignee = assigneeText(session.assignee);
            const left = sessionEnded(session);
            const reply = replyBySession.get(session.sessionId);
            const dot = replyDotVisible(
              reply?.needsReply === true,
              reply?.latestInboundTs ?? null,
              seen[session.sessionId] ?? null,
            );
            return (
              <button
                key={session.sessionId}
                type="button"
                data-testid="waterfall-session"
                data-session-id={session.sessionId}
                data-selected={selected ? 'true' : 'false'}
                data-needs-reply={reply?.needsReply === true ? 'true' : 'false'}
                data-reply-dot={dot ? 'true' : 'false'}
                data-ended={left ? 'true' : 'false'}
                className={`block w-full border-b px-2 py-2 text-left text-xs ${
                  reply?.needsReply === true ? 'border-l-4 border-warning' : 'border-border'
                } ${selected ? 'bg-muted' : ''} ${left ? 'opacity-55' : ''}`}
                onClick={() => selectSession(session.sessionId)}
              >
                <span className="font-mono text-[10px] text-text-tertiary">{session.platform}</span>
                <div className="flex items-center gap-1" title={heading.primary}>
                  <span className="min-w-0 truncate">{heading.primary}</span>
                  {left ? (
                    <span
                      className="shrink-0 rounded border border-border px-1 text-[10px] text-text-tertiary"
                      data-testid="waterfall-ended-badge"
                    >
                      已離開
                    </span>
                  ) : null}
                  {reply?.needsReply === true ? (
                    <span
                      className="shrink-0 rounded border border-warning px-1 text-[10px] text-warning"
                      data-testid="waterfall-reply-chip"
                    >
                      {PENDING_REPLY_CHIP}
                    </span>
                  ) : null}
                  {dot ? (
                    <span
                      className="inline-block h-2 w-2 shrink-0 rounded-full bg-warning"
                      data-testid="waterfall-reply-dot"
                    />
                  ) : null}
                </div>
                {preview ? (
                  <div className="truncate text-text-tertiary" data-testid="waterfall-preview" title={preview}>
                    {preview}
                  </div>
                ) : null}
                {heading.peerKey ? (
                  <div className="truncate font-mono text-[10px]" data-testid="waterfall-peer" title={heading.peerKey}>
                    {heading.peerKey}
                  </div>
                ) : null}
                <span
                  data-testid="waterfall-row-mode"
                  data-mode={session.mode}
                  className={`inline-block rounded border border-border px-1 ${
                    session.mode === 'human' ? 'text-warning' : 'text-text-tertiary'
                  }`}
                >
                  {session.mode === 'human' ? '人工' : '自動'}
                </span>
                {rowAssignee ? (
                  <span className="mt-0.5 block truncate text-[10px]" data-testid="waterfall-row-assignee">
                    {rowAssignee}
                  </span>
                ) : null}
              </button>
            );
          })}
          {ordered.length === 0 ? (
            <p className="p-2 text-xs text-text-tertiary">
              {error
                ? '讀不到導流 session（不是「還沒有」）；修好連線後按重試。'
                : '尚無導流 session（runner 上傳後會出現）。'}
            </p>
          ) : null}
        </aside>

        <section className="flex min-h-0 flex-col overflow-hidden rounded border border-border">
          {detail ? (
            <div
              className="flex shrink-0 flex-wrap items-center gap-1 border-b border-border px-2 py-1.5"
              data-testid="waterfall-detail"
            >
              <span
                className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px]"
                data-testid="waterfall-detail-platform"
              >
                {detail.platform}
              </span>
              <span
                className="rounded border border-border px-1.5 py-0.5 text-[10px]"
                data-testid="waterfall-detail-life"
              >
                {detail.life === 'ended' ? '已離開' : '進行中'}
              </span>
              <span
                className="rounded border border-border px-1.5 py-0.5 text-[10px]"
                data-testid="waterfall-detail-mode"
                data-mode={detail.mode}
              >
                {detail.mode === 'human' ? '人工' : '自動'}
              </span>
              {detail.title ? (
                <span className="text-[10px]" data-testid="waterfall-detail-title">
                  {detail.title}
                </span>
              ) : null}
              {detail.peerKey ? (
                <span className="font-mono text-[10px] text-text-tertiary" data-testid="waterfall-detail-peer">
                  {detail.peerKey}
                </span>
              ) : null}
              {detail.assignee ? (
                <span className="text-[10px] text-text-tertiary" data-testid="waterfall-detail-assignee">
                  {detail.assignee}
                </span>
              ) : null}
              {detail.lastActivity ? (
                <span className="text-[10px] text-text-tertiary" data-testid="waterfall-detail-activity">
                  {formatRelativeZhTw(detail.lastActivity, nowMs)}
                </span>
              ) : null}
              {detail.room ? (
                <span className="font-mono text-[10px]" data-testid="waterfall-detail-room">
                  {detail.room}
                </span>
              ) : null}
              {detail.outcomes.map((event) => (
                <span
                  key={event}
                  className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px]"
                  data-testid="waterfall-outcome"
                  data-event={event}
                >
                  {event}
                </span>
              ))}
            </div>
          ) : null}
          <div
            ref={streamRef}
            onScroll={onStreamScroll}
            className="min-h-0 flex-1 space-y-2 overflow-auto p-3"
            data-testid="waterfall-stream"
          >
            {shown.map((message) => {
              const inbound = message.direction === 'in';
              const when = formatRelativeZhTw(message.ts, nowMs);
              return (
                <div
                  key={message.id}
                  className={`flex ${inbound ? 'justify-start' : 'justify-end'}`}
                  data-testid="waterfall-bubble"
                  data-direction={inbound ? 'in' : 'out'}
                  data-pending={message.pendingSend ? 'true' : 'false'}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-3 py-1.5 text-sm ${
                      inbound ? 'bg-muted' : 'border border-border bg-card'
                    }`}
                  >
                    <p className="whitespace-pre-wrap break-words">{message.body}</p>
                    {when || message.pendingSend ? (
                      <p className="mt-1 text-[10px] text-text-tertiary" data-testid="waterfall-time">
                        {when}
                        {message.pendingSend ? `${when ? ' · ' : ''}排隊中` : ''}
                      </p>
                    ) : null}
                  </div>
                </div>
              );
            })}
            {shown.length === 0 ? (
              <p className="text-xs text-text-tertiary">
                {error ? '讀不到訊息（不是「沒有對話」）。' : '尚無訊息。'}
              </p>
            ) : null}
          </div>
          {active ? (
            <div className="flex shrink-0 flex-col gap-2 border-t border-border p-2">
              {showTakeoverWarn ? (
                <p className="text-xs text-amber-400" role="alert" data-testid="human-takeover-warn">
                  這條還在自動。送出後會改成人工，自動回覆會停。
                </p>
              ) : null}
              {composerError ? (
                <p className="text-xs text-danger" role="alert" data-testid="waterfall-composer-error">
                  {composerError}
                </p>
              ) : null}
              {phraseView.menu.length > 0 ? (
                <ul className="cui-command-options" data-testid="waterfall-phrase-menu" aria-label="片語">
                  {phraseView.menu.map((item) => (
                    <li key={item.key}>
                      <button
                        type="button"
                        className="cui-command-option min-h-11"
                        data-testid="waterfall-phrase-option"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => pickMenuItem(item)}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              {phraseNote ?? phraseView.hint ? (
                <p className="cui-command-hint" role="status" data-testid="waterfall-phrase-hint">
                  {phraseNote ?? phraseView.hint}
                </p>
              ) : null}
              <div className="flex gap-2">
                <textarea
                  className="min-h-[2.75rem] min-w-0 flex-1 resize-y rounded border border-border bg-transparent px-2 py-1.5 text-sm"
                  rows={2}
                  value={draft}
                  data-testid="waterfall-composer"
                  placeholder={active.mode === 'human' ? '人工回覆…（/ 片語）' : '送出前會先問要不要改人工（/ 片語）'}
                  onFocus={() => setPhraseTick((n) => n + 1)}
                  onChange={(event) => {
                    const sessionId = active.sessionId;
                    setPhraseNote(null);
                    setDrafts((prev) => rememberDraft(prev, sessionId, event.target.value));
                  }}
                  onKeyDown={onComposerKeyDown}
                />
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    disabled={phraseSubmitDisabled(phraseView, draft, busy)}
                    className="rounded border border-border px-3 py-1.5 text-sm"
                    data-testid="waterfall-send"
                    onClick={() => applyPhrase(freshPhraseView())}
                  >
                    {phraseSubmitLabel(phraseView.enter, showTakeoverWarn)}
                  </button>
                  {showTakeoverWarn ? (
                    <button
                      type="button"
                      className="rounded border border-border px-3 py-1.5 text-sm"
                      data-testid="human-takeover-cancel"
                      onClick={() => setArmed('none')}
                    >
                      留在自動
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          ) : (
            <p className="shrink-0 border-t border-border p-2 text-xs text-text-tertiary">
              選左側一條對話才能人工回覆。
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
